import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useParams } from 'react-router-dom';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import Dashboard from './pages/DashboardPage';
import LandingPage from './pages/LandingPage';
import PendingApprovalPage from './pages/PendingApprovalPage';
import BecomeMasterTrader from './pages/BecomeMasterTrader';
import MasterDetailsPage from './pages/MasterDetailsPage';
import SignalsPage from './pages/SignalsPage';
import NotificationsPage from './pages/NotificationsPage';
import StatisticsPage from './pages/StatisticsPage';
import FollowersPage from './pages/FollowersPage';
import AIChatPage from './pages/AIChatPage';
import { useAuthStore } from './stores';
import { useApi } from './hooks/useApi';
import { useTheme } from './hooks/useTheme';
import { useWebSocket } from './hooks/useWebSocket';
import { Loader } from './components/Common/Loader';
import ConnectionBrokenAlert from './components/Common/ConnectionBrokenAlert';
import RiskDisclosureAgreementModal from './components/Common/RiskDisclosureAgreementModal';
import MasterPilotAgreementModal from './components/Common/MasterPilotAgreementModal';
import SubscriptionGatedRoute from './components/Common/SubscriptionGatedRoute';
import { FiLogOut, FiShield } from 'react-icons/fi';
import { useQueryClient } from '@tanstack/react-query';
import { socketService } from './services/socketService';

// Banner shown when admin is impersonating a user
function ImpersonationBanner() {
  const { adminToken, adminUser, exitImpersonation } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  if (!adminToken) return null;

  const handleExit = () => {
    exitImpersonation();
    const restoredUser = useAuthStore.getState().user;
    queryClient.clear(); // wipe all cached data so admin sees their own data
    if (restoredUser) {
      queryClient.setQueryData(['auth', 'me'], { success: true, user: restoredUser });
    }
    localStorage.removeItem('selectedBrokerId');
    socketService.disconnect();
    navigate('/dashboard');
  };

  return (
    <div className="fixed top-2 left-1/2 z-[9999] w-[min(280px,calc(100vw-1.5rem))] -translate-x-1/2 overflow-hidden rounded-2xl border border-white/20 bg-[#246BF5]/85 text-sm font-semibold text-white shadow-[0_12px_40px_rgba(36,107,245,0.45),inset_0_1px_0_rgba(255,255,255,0.25)] backdrop-blur-xl">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent" />
      <button
        onClick={handleExit}
        className="flex w-full items-center justify-center gap-1.5 px-4 py-2.5 transition-colors hover:bg-white/10"
      >
        <FiLogOut className="h-4 w-4" />
        Exit to Admin Panel
      </button>
    </div>
  );
}

function ReferralRedirect() {
  const { referralCode = '' } = useParams();

  useEffect(() => {
    if (/^[a-f\d]{24}$/i.test(referralCode)) {
      localStorage.setItem('referralId', referralCode);
    }
  }, [referralCode]);

  return <Navigate to={`/register?ref=${encodeURIComponent(referralCode)}`} replace />;
}

function App() {
  const { user, setUser, setToken, token } = useAuthStore();
  const { theme } = useTheme();
  const persistedToken = localStorage.getItem('token');
  const authToken = token || persistedToken;

  // Load token from localStorage on mount if not already loaded
  useEffect(() => {
    if (persistedToken && !token) {
      setToken(persistedToken);
    }
  }, [token, setToken, persistedToken]);

  const { data: userData, isLoading: isLoadingUser } = useApi.useCurrentUser({
    enabled: !!authToken && !user,
    retry: 1,
    retryDelay: 1000,
  });

  // Initialize WebSocket connection
  useWebSocket();

  useEffect(() => {
    if (userData?.user) {
      setUser(userData.user);
    }
  }, [userData, setUser]);

  // Only clear token on confirmed auth failures (handled by API client interceptor)
  // Don't clear token here - let the API client handle 401 errors intelligently

  if (isLoadingUser && authToken) {
    return <Loader fullScreen message="Loading..." />;
  }

  return (
    <Router>
      <div className="App bg-background text-foreground min-h-screen" data-theme={theme}>
        <ImpersonationBanner />
        <ConnectionBrokenAlert />
        {user && authToken && user.role === 'follower' && !user.isAgreedwithAgreement && (
          <RiskDisclosureAgreementModal />
        )}
        {user && authToken && user.role === 'master' && !user.isMasterAgreedwithAgreement && (
          <MasterPilotAgreementModal />
        )}
        <Routes>
          {/* <Route path="/" element={<LandingPage />} /> */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/ref/:referralCode" element={<ReferralRedirect />} />
          <Route
            path="/login"
            element={!user ? <Login /> : <Navigate to="/dashboard/overview" />}
          />
          <Route
            path="/register"
            element={!user ? <Register /> : <Navigate to="/dashboard/overview" />}
          />
          <Route
            path="/become-master-trader"
            element={<BecomeMasterTrader />}
          />
          <Route
            path="/register/pending"
            element={<PendingApprovalPage />}
          />
          <Route
            path="/forgot-password"
            element={!user ? <ForgotPassword /> : <Navigate to="/dashboard/overview" />}
          />
          <Route
            path="/reset-password/:resetToken"
            element={!user ? <ResetPassword /> : <Navigate to="/dashboard/overview" />}
          />
          <Route
            path="/dashboard/*"
            element={
              user && user.role !== 'master_pending' ? (
                <Dashboard />
              ) : user?.role === 'master_pending' ? (
                <Navigate to="/register/pending" />
              ) : authToken ? (
                <Loader fullScreen message="Loading..." />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/master-details/:masterId"
            element={
              user && user.role !== 'master_pending' ? (
                <MasterDetailsPage />
              ) : user?.role === 'master_pending' ? (
                <Navigate to="/register/pending" />
              ) : authToken ? (
                <Loader fullScreen message="Loading..." />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/community"
            element={
              user && user.role !== 'master_pending' ? (
                <SignalsPage />
              ) : user?.role === 'master_pending' ? (
                <Navigate to="/register/pending" />
              ) : authToken ? (
                <Loader fullScreen message="Loading..." />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/notifications"
            element={
              user && user.role !== 'master_pending' ? (
                <NotificationsPage />
              ) : user?.role === 'master_pending' ? (
                <Navigate to="/register/pending" />
              ) : authToken ? (
                <Loader fullScreen message="Loading..." />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/followers"
            element={
              user && user.role !== 'master_pending' ? (
                <FollowersPage />
              ) : user?.role === 'master_pending' ? (
                <Navigate to="/register/pending" />
              ) : authToken ? (
                <Loader fullScreen message="Loading..." />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/dashboard/statistics"
            element={
              user && (user.role === 'admin' || user.role === 'master') ? (
                <StatisticsPage />
              ) : user?.role === 'master_pending' ? (
                <Navigate to="/register/pending" />
              ) : authToken ? (
                <Loader fullScreen message="Loading..." />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/ai-chat"
            element={
              user ? (
                <SubscriptionGatedRoute>
                  <AIChatPage />
                </SubscriptionGatedRoute>
              ) : (
                <AIChatPage />
              )
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
